import { motion } from "framer-motion";
import { ArrowRight, Lock, Wifi, Cpu } from "lucide-react";
import { Button } from "@/components/ui/button";

export const HeroSection = () => {
  const scrollToContact = () => {
    const element = document.querySelector("#contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToServices = () => {
    const element = document.querySelector("#services");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section">

      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1772112334845-86016056137b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBzbWFydCUyMGhvbWUlMjBsaXZpbmclMjByb29tJTIwbmlnaHQlMjBtb29keSUyMGludGVyaW9yfGVufDB8fHx8MTc3MjU5Mjc1MXww&ixlib=rb-4.1.0&q=85"
          alt="Luxury smart home living room"
          className="w-full h-full object-cover opacity-40" />

        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/70 to-[#050505]" />
      </div>

      {/* Hero Glow Effect */}
      <div className="hero-glow" />

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pt-32 pb-20">
        <div className="max-w-4xl">
          {/* Label */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-center gap-3 mb-8">

            <div className="h-px w-12 bg-[#5e5ce6]" />
            <span className="text-xs uppercase tracking-[0.2em] text-[#5e5ce6] font-medium">
              Smart Home Integration
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] text-white mb-6"
            data-testid="hero-headline">

            The Private Smart Home,{" "}
            <span className="text-[#5e5ce6]">Perfected.</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-base md:text-lg text-white/60 leading-relaxed max-w-2xl mb-10"
            data-testid="hero-subheadline">From secure networking, security, to local AI that respects your privacy. We modernize your living space with technology that works for you and your family.



          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4">

            <Button
              onClick={scrollToContact}
              className="group bg-white text-black hover:bg-white/90 rounded-full px-8 py-6 text-sm font-semibold shadow-[0_0_30px_rgba(255,255,255,0.2)] hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] transition-shadow duration-300"
              data-testid="hero-cta-primary">

              Book a Consultation
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
            <Button
              onClick={scrollToServices}
              variant="ghost"
              className="bg-white/5 text-white hover:bg-white/10 backdrop-blur-md border border-white/10 rounded-full px-8 py-6 text-sm font-medium transition-colors duration-300"
              data-testid="hero-cta-secondary">

              Explore Services
            </Button>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="flex flex-wrap items-center gap-8 mt-16 pt-16 border-t border-white/5">

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center border border-white/10">
                <Lock className="w-5 h-5 text-[#5e5ce6]" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">100% Local</p>
                <p className="text-xs text-white/40">No cloud required</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center border border-white/10">
                <Wifi className="w-5 h-5 text-[#5e5ce6]" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Military-Grade</p>
                <p className="text-xs text-white/40">Network security</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center border border-white/10">
                <Cpu className="w-5 h-5 text-[#5e5ce6]" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Private AI</p>
                <p className="text-xs text-white/40">Voice control</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10">

        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 rounded-full border border-white/20 flex items-start justify-center p-2">

          <div className="w-1 h-2 bg-white/40 rounded-full" />
        </motion.div>
      </motion.div>
    </section>);

};